/**
 * News API client for the SPA.
 *
 * Talks to BFF endpoints under /api/news/*. All endpoints are public
 * (no auth Day-1) but rate-limited per-IP server-side (Group B spec).
 *
 * Network errors surface as Error throws — consumer renders banner UI.
 */

import type { NewsCardData, NewsAiDisclosure } from '../components/news/NewsThreadCard';
import type { PlayfulTag } from '../i18n/hotnews';

export type { NewsAiDisclosure };

export interface NewsFeedResponse {
  items: NewsCardData[];
  next_cursor: string | null;
}

export interface NewsDetailResponse {
  id: string;
  slug: string;
  source_id: string;
  source_name: string;
  source_url: string;
  headline: string;
  ai_summary: string | null;
  ai_summary_status: 'pending' | 'generated' | 'failed' | 'rejected';
  raw_excerpt: string | null;
  topic: string | null;
  detail_mode: 'case_event' | 'new_law' | 'mixed';
  primary_vbpl_doc_id: string | null;
  primary_vbpl_provision: string | null;
  tags: PlayfulTag[];
  published_at: number;
  ingested_at: number;
  /** Luat AI 2026 Dieu 28 transparency block (backend always sends). */
  ai_disclosure?: NewsAiDisclosure;
}

export interface FetchFeedParams {
  topic?: string;
  source?: string;
  tag?: string;
  cursor?: string;
  limit?: number;
}

// SWR cache for news feed - returns cached page-1 instantly on revisit,
// refreshes in background. Skips cache for cursor pages (load-more).
const FEED_CACHE_TTL_MS = 5 * 60 * 1000;
type FeedCacheEntry = { data: NewsFeedResponse; at: number };
const feedCache = new Map<string, FeedCacheEntry>();

function feedCacheKey(p: FetchFeedParams): string {
  return JSON.stringify({ t: p.topic ?? '', s: p.source ?? '', g: p.tag ?? '', l: p.limit ?? 0 });
}

export function getCachedNewsFeed(params: FetchFeedParams = {}): NewsFeedResponse | null {
  if (params.cursor) return null;
  const hit = feedCache.get(feedCacheKey(params));
  if (!hit) return null;
  if (Date.now() - hit.at > FEED_CACHE_TTL_MS) return null;
  return hit.data;
}

export async function fetchNewsFeed(params: FetchFeedParams = {}): Promise<NewsFeedResponse> {
  const url = new URL('/api/news/feed', window.location.origin);
  if (params.topic) url.searchParams.set('topic', params.topic);
  if (params.source) url.searchParams.set('source', params.source);
  if (params.tag) url.searchParams.set('tag', params.tag);
  if (params.cursor) url.searchParams.set('cursor', params.cursor);
  if (params.limit) url.searchParams.set('limit', String(params.limit));

  const res = await fetch(url.toString(), {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`feed_${res.status}`);
  }
  const data = (await res.json()) as NewsFeedResponse;
  if (!params.cursor) {
    feedCache.set(feedCacheKey(params), { data, at: Date.now() });
  }
  return data;
}

export async function fetchNewsDetail(slug: string): Promise<NewsDetailResponse> {
  const url = `/api/news/${encodeURIComponent(slug)}`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (res.status === 404) {
    throw new Error('not_found');
  }
  if (!res.ok) {
    throw new Error(`detail_${res.status}`);
  }
  return (await res.json()) as NewsDetailResponse;
}

export interface SubscribeDigestInput {
  email: string;
  topics: string[];
  consent_text_version: string;
  privacy_policy_version: string;
  consent_user_agent: string;
  consent_lang: 'vi' | 'en';
}

export async function subscribeNewsDigest(input: SubscribeDigestInput): Promise<{ ok: true }> {
  const res = await fetch('/api/news/digest/subscribe', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(input),
  });
  if (res.status === 429) {
    throw new Error('rate_limit');
  }
  if (!res.ok) {
    let msg = `subscribe_${res.status}`;
    try {
      const body = (await res.json()) as { error?: { code?: string; message?: string } };
      if (body.error?.code) msg = body.error.code;
    } catch {
      // ignore
    }
    throw new Error(msg);
  }
  return { ok: true };
}
